// pages/ReportsList.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllReports,
  getReportDetails,
  updateReportStatus,
} from "../../api/questionReports";
import Navigation from "../Navigation";
import {
  XMarkIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import Notification from "../Notification";
import Loading from "../loading";
import ReportDetailsModal from "../modals/reportDitailsModal";

const ReportsList = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [refreshTokens, setRefreshTokens] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});

  // ---------- دریافت لیست گزارش‌ها ----------
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const data = {
          page: 1,
          limit: 20,
        };
        const res = await getAllReports(data);
        setReports(res.data.reports || []);
      } catch (err) {
        console.error("❌ خطا در دریافت گزارش‌ها:", err);
        showError("خطا در دریافت گزارش‌ها");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [refreshTokens]);

  // ---------- دریافت جزئیات یک گزارش ----------
  const fetchReportDetails = async (reportId) => {
    setDetailsLoading(true);
    try {
      const res = await getReportDetails(reportId);
      setReportDetails(res.data.report || null);
    } catch (err) {
      console.error("❌ خطا در دریافت جزئیات گزارش:", err);
      showError("خطا در دریافت جزئیات گزارش");
    } finally {
      setDetailsLoading(false);
    }
  };

  // ---------- باز کردن مودال جزئیات ----------
  const handleReportClick = async (report) => {
    setSelectedReport(report);
  };

  // ---------- بستن مودال ----------
  const closeModal = () => {
    setSelectedReport(null);
    setReportDetails(null);
  };

  // ---------- اعلان‌ها ----------
  const showSuccess = (msg) => {
    setNotificationProps({ message: msg, type: "success", duration: 3000 });
    setShowNotification(true);
  };

  const showError = (msg) => {
    setNotificationProps({ message: msg, type: "error", duration: 4000 });
    setShowNotification(true);
  };

  // ---------- تغییر وضعیت گزارش ----------
  const handleStatusChange = async (reportId, newStatus) => {
    try {
      await updateReportStatus(reportId, { status: newStatus });
      showSuccess(
        `وضعیت گزارش با موفقیت به "${getStatusLabel(newStatus)}" تغییر یافت.`,
      );
      setRefreshTokens((prev) => !prev);
      if (selectedReport) {
        await fetchReportDetails(selectedReport._id);
      }
    } catch (err) {
      console.error("❌ خطا در تغییر وضعیت:", err);
      showError("خطا در تغییر وضعیت گزارش");
    }
  };

  // ---------- دریافت برچسب وضعیت ----------
  const getStatusLabel = (status) => {
    const map = {
      pending: "در انتظار بررسی",
      reviewed: "بررسی شده",
      accepted: "تأیید شده",
      rejected: "رد شده",
      fixed: "اصلاح شده",
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-700",
      reviewed: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      fixed: "bg-purple-100 text-purple-700",
    };
    return map[status] || "bg-gray-100 text-gray-700";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <ClockIcon className="w-4 h-4" />;
      case "reviewed":
        return <EyeIcon className="w-4 h-4" />;
      case "accepted":
        return <CheckCircleIcon className="w-4 h-4" />;
      case "rejected":
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <DocumentTextIcon className="w-4 h-4" />;
    }
  };

  // ---------- فرمت تاریخ ----------
  const formatDate = (date) =>
    new Date(date).toLocaleString("fa-IR", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // ---------- گروه‌بندی گزارش‌ها بر اساس وضعیت ----------
  const pendingReports = reports
    .filter((report) => report.status === "pending")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const reviewedReports = reports
    .filter((report) => report.status === "reviewed")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const acceptedReports = reports
    .filter(
      (report) => report.status === "accepted" || report.status === "fixed",
    )
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const rejectedReports = reports
    .filter((report) => report.status === "rejected")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // ---------- رندر ----------
  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      {showNotification && (
        <Notification
          {...notificationProps}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}

      {/* مودال نمایش جزئیات گزارش */}
      {selectedReport  ? (
        <div className="container mx-auto px-4">
          <div className="flex flex-row w-full lg:flex-row gap-6">
            <div className="w-full md:w-1/4 mb-6 lg:mb-0">
              <Navigation />
            </div>

            <div className="w-full md:w-3/4">
              <ReportDetailsModal
                selectedReport={selectedReport}
                detailsLoading={detailsLoading}
                onClose={closeModal}
                onStatusChange={handleStatusChange}
                onRefresh={() => setRefreshTokens((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4">
          <div className="flex flex-row w-full lg:flex-row gap-6">
            {/* نوار کناری */}
            <div className="w-full md:w-1/4 mb-6 lg:mb-0">
              <Navigation />
            </div>

            {/* محتوای اصلی */}
            <div className="w-full md:w-3/4">
              <div className="bg-white rounded-2xl shadow-lg border-gray-100 overflow-hidden">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-800">
                      گزارش‌های کاربران ({reports.length})
                    </h2>
                    <button
                      onClick={() => setRefreshTokens((prev) => !prev)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      🔄 به‌روزرسانی
                    </button>
                  </div>

                  {reports.length > 0 ? (
                    <div className="space-y-8">
                      {/* بخش اول: گزارش‌های در انتظار بررسی */}
                      {pendingReports.length > 0 && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-800 my-4 pb-2 border-b border-yellow-200 flex items-center gap-2">
                            <ClockIcon className="w-5 h-5 text-yellow-500" />
                            در انتظار بررسی ({pendingReports.length})
                          </h3>
                          <div className="space-y-4">
                            {pendingReports.map((report) => (
                              <ReportCard
                                key={report._id}
                                report={report}
                                onClick={() => handleReportClick(report)}
                                formatDate={formatDate}
                                getStatusLabel={getStatusLabel}
                                getStatusColor={getStatusColor}
                                getStatusIcon={getStatusIcon}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* بخش دوم: گزارش‌های بررسی شده */}
                      {reviewedReports.length > 0 && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-800 my-4 pb-2 border-b border-blue-200 flex items-center gap-2">
                            <EyeIcon className="w-5 h-5 text-blue-500" />
                            در حال بررسی ({reviewedReports.length})
                          </h3>
                          <div className="space-y-4">
                            {reviewedReports.map((report) => (
                              <ReportCard
                                key={report._id}
                                report={report}
                                onClick={() => handleReportClick(report)}
                                formatDate={formatDate}
                                getStatusLabel={getStatusLabel}
                                getStatusColor={getStatusColor}
                                getStatusIcon={getStatusIcon}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* بخش سوم: گزارش‌های تأیید شده */}
                      {acceptedReports.length > 0 && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-800 my-4 pb-2 border-b border-green-200 flex items-center gap-2">
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                            تأیید شده ({acceptedReports.length})
                          </h3>
                          <div className="space-y-4">
                            {acceptedReports.map((report) => (
                              <ReportCard
                                key={report._id}
                                report={report}
                                onClick={() => handleReportClick(report)}
                                formatDate={formatDate}
                                getStatusLabel={getStatusLabel}
                                getStatusColor={getStatusColor}
                                getStatusIcon={getStatusIcon}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* بخش چهارم: گزارش‌های رد شده */}
                      {rejectedReports.length > 0 && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-800 my-4 pb-2 border-b border-red-200 flex items-center gap-2">
                            <XCircleIcon className="w-5 h-5 text-red-500" />
                            رد شده ({rejectedReports.length})
                          </h3>
                          <div className="space-y-4">
                            {rejectedReports.map((report) => (
                              <ReportCard
                                key={report._id}
                                report={report}
                                onClick={() => handleReportClick(report)}
                                formatDate={formatDate}
                                getStatusLabel={getStatusLabel}
                                getStatusColor={getStatusColor}
                                getStatusIcon={getStatusIcon}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <DocumentTextIcon className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-700 mb-2">
                        گزارشی یافت نشد
                      </h3>
                      <p className="text-gray-500 max-w-md mx-auto">
                        هنوز هیچ گزارشی از طرف کاربران ثبت نشده است.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== کامپوننت کارت گزارش ==========
const ReportCard = ({
  report,
  onClick,
  formatDate,
  getStatusLabel,
  getStatusColor,
  getStatusIcon,
}) => {
  const question = report.questionId || {};
  const lesson = question.lessonId || {};

  return (
    <div
      className="border border-gray-200 bg-white hover:shadow-lg transition-all p-5 rounded-xl cursor-pointer transform hover:-translate-y-0.5"
      onClick={onClick}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          {/* عنوان گزارش */}
          <h3 className="font-bold text-gray-800 text-lg mb-2">
            {report.title || "گزارش جدید"}
          </h3>

          {/* متن سوال (خلاصه) */}
          <div
            className="text-sm text-gray-600 line-clamp-2 mb-2"
            dangerouslySetInnerHTML={{
              __html: question.content
                ? question.content.substring(0, 150) + "..."
                : "متن سوال موجود نیست",
            }}
          />
        </div>

        {/* وضعیت */}
        <span
          className={`px-3 py-1 text-xs rounded-full font-medium flex items-center gap-1 ${getStatusColor(
            report.status,
          )}`}
        >
          {getStatusIcon(report.status)}
          {getStatusLabel(report.status)}
        </span>
      </div>

      {/* اطلاعات سوال */}
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-t border-gray-100 pt-3">
        <span className="flex items-center gap-1">
          <span className="text-gray-400">📚</span>
          {lesson.title || "نامشخص"}
        </span>
        <span className="flex items-center gap-1">
          <span className="text-gray-400">🎓</span>
          پایه {question.grade || "نامشخص"}
        </span>
        <span className="flex items-center gap-1">
          <span className="text-gray-400">📖</span>
          {question.bookName || question.book || "نامشخص"}
        </span>
        <span className="flex items-center gap-1 mr-auto">
          <span className="text-gray-400">📅</span>
          {formatDate(report.created_at)}
        </span>
      </div>

      {/* دکمه مشاهده کامل */}
      <div className="mt-3 flex justify-end">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <EyeIcon className="w-4 h-4" />
          مشاهده متن کامل گزارش
        </button>
      </div>
    </div>
  );
};

export default ReportsList;
